import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Clock, AlertTriangle, ChevronRight } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => { setServices(r.data); setLoading(false); }).catch(console.error);
  }, []);

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* Hero */}
      <section data-testid="services-hero" className="relative min-h-[50vh] flex items-end pt-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ background: "radial-gradient(circle at 70% 30%, rgba(59,91,255,0.08) 0%, transparent 60%)" }} className="absolute inset-0" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-14 w-full relative">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Oferta</p>
            <h1 className="font-anton text-6xl sm:text-8xl md:text-[10vw] text-white uppercase leading-none">
              USŁUGI
            </h1>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="text-white/50 text-sm mt-4 font-outfit max-w-lg">
            Ceny zależą od długości włosów (stopnia 1, 2 lub 3).{" "}
            <Link to="/cennik" className="text-violet-400 hover:text-white transition-colors">
              Sprawdź stopnie →
            </Link>
          </motion.p>
        </div>
      </section>

      {/* STOPNIE Guide */}
      <section className="py-16 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center border border-white/8 p-6 md:p-8 lg:p-12" style={{ background: "#0A0A0A" }}>
          {/* Text */}
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-3">Przewodnik cenowy</p>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase leading-none mb-2">STOPNIE</h2>
            <div className="section-marker mb-6" />
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Ceny usług zależą od długości Twoich włosów. Określ swój stopień - ułatwi to wycenę i podanie czasu trwania zabiegu.
            </p>
            <div className="space-y-4 mb-6">
              {[
                { n: "1", label: "STOPNIA", desc: "Krótkie i średnie - do ramion" },
                { n: "2", label: "STOPNIA", desc: "Długie - poniżej ramion" },
                { n: "3", label: "STOPNIA", desc: "Bardzo długie - do pasa i dłużej" },
              ].map((s, i) => (
                <div key={s.n} className="flex items-center gap-4 border-b border-white/5 pb-4">
                  <div className="font-anton text-3xl gradient-text leading-none w-6">{s.n}</div>
                  <div>
                    <span className="font-anton text-lg text-white">{s.n} {s.label}</span>
                    <span className="text-white/40 text-xs font-outfit ml-3">{s.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Dense hair note */}
            <div className="border border-yellow-500/20 p-4" style={{ background: "rgba(234,179,8,0.04)" }}>
              <p className="text-yellow-200/70 text-xs leading-relaxed font-outfit">
                <span className="text-yellow-400 font-semibold">Uwaga:</span> Podane ceny są cenami orientacyjnymi. Jeżeli Twoje włosy są wyjątkowo gęste, cena może być wyższa - ostateczna wycena po ocenie stanu i gęstości włosów na miejscu.
              </p>
            </div>
          </div>

          {/* Image — border ON the container (outside photo) */}
          <div className="relative overflow-hidden border-2 border-violet-600/25" style={{ background: "#111111" }}>
            <img
              src="https://customer-assets.emergentagent.com/job_fryzjer-artysta/artifacts/1a77oron_IMG_8489.jpg"
              alt="Stopnie długości włosów - Milena Bachara"
              className="w-full aspect-square object-contain object-top"
            />
            {/* Gradient on right for label readability */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(to right, transparent 50%, rgba(0,0,0,0.6) 100%)" }} />

            {/* Level markers */}
            {[
              { top: "55%", n: "1", label: "STOPNIA", desc: "Do ramion" },
              { top: "69%", n: "2", label: "STOPNIA", desc: "Poniżej ramion" },
              { top: "82%", n: "3", label: "STOPNIA", desc: "Do pasa+" },
            ].map(m => (
              <div key={m.n} className="absolute"
                style={{ top: m.top, left: 0, right: 0, transform: "translateY(-50%)" }}>
                <div className="absolute flex items-center" style={{ left: "28%", right: 0 }}>
                  <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.65)" }} />
                  <div className="flex items-center gap-1.5 flex-shrink-0 mr-2"
                    style={{ background: "rgba(0,0,0,0.82)", border: "1px solid rgba(255,255,255,0.25)", padding: "3px 10px" }}>
                    <span className="font-anton text-sm gradient-text leading-none">{m.n}</span>
                    <span className="font-outfit text-xs text-white uppercase tracking-[0.08em]">{m.label}</span>
                    <span className="text-white/45 text-xs font-outfit hidden sm:inline">- {m.desc}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services list */}
      <section data-testid="services-list" className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 pb-24">
        {loading ? (
          <div className="py-20 text-center text-white/40">Ładowanie...</div>
        ) : (
          <div className="divide-y divide-white/5">
            {services.map((s, i) => {
              const isOpen = expanded === s.id;
              const minPrice = s.rows[0]?.price || "";
              const maxPrice = s.rows[s.rows.length - 1]?.price || "";
              const minDur = s.durations[0] || 60;
              const maxDur = s.durations[s.durations.length - 1] || 60;

              return (
                <FadeUp key={s.id} delay={i * 0.04}>
                  <div className="py-6">
                    {/* Header row - clickable to expand */}
                    <button
                      data-testid={`service-expand-${i}`}
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="w-full text-left grid md:grid-cols-[auto_1fr_auto] gap-6 items-center group">
                      {/* Number */}
                      <div className="font-anton text-5xl leading-none hidden md:block"
                        style={{ color: "rgba(139,0,255,0.12)", WebkitTextStroke: "1px rgba(139,0,255,0.15)" }}>
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      {/* Name + duration */}
                      <div>
                        <h2 className="font-anton text-2xl md:text-3xl text-white uppercase leading-tight group-hover:text-violet-300 transition-colors">
                          {s.subtitle}
                        </h2>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-white/30 text-xs font-outfit flex items-center gap-1">
                            <Clock size={11} /> {minDur === maxDur ? `${minDur} min` : `${minDur}-${maxDur} min`}
                          </span>
                          {s.warning && (
                            <span className="text-yellow-500/60 text-xs flex items-center gap-1">
                              <AlertTriangle size={11} /> Konsultacja wymagana
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Price + toggle */}
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-white/30 text-xs uppercase tracking-[0.15em] font-outfit">od</div>
                          <div className="font-anton text-2xl text-white">{minPrice}</div>
                        </div>
                        <ChevronRight size={16} className={`text-white/30 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="mt-6 ml-0 md:ml-20 border border-white/5 p-6" style={{ background: "#0A0A0A" }}>
                          {/* Price rows with durations */}
                          <div className="space-y-0 mb-5">
                            {s.rows.map((row, j) => {
                              const dur = s.durations[j] || 60;
                              return (
                                <div key={j} className={`flex justify-between items-center py-3 ${j < s.rows.length - 1 ? "border-b border-white/5" : ""}`}>
                                  <div>
                                    <span className="font-outfit text-sm text-white/80">{row.label}</span>
                                    <span className="text-white/25 text-xs ml-2 font-outfit">
                                      <Clock size={9} className="inline mr-1" />
                                      {Math.floor(dur / 60) > 0 ? `${Math.floor(dur / 60)}h ` : ""}{dur % 60 > 0 ? `${dur % 60}min` : ""}
                                    </span>
                                  </div>
                                  <span className="font-anton text-xl text-white">{row.price}</span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Warning */}
                          {s.warning && (
                            <div className="flex items-start gap-2 mb-4 p-3 border border-yellow-500/20"
                              style={{ background: "rgba(234,179,8,0.04)" }}>
                              <AlertTriangle size={13} className="text-yellow-400/70 flex-shrink-0 mt-0.5" />
                              <p className="text-yellow-200/60 text-xs leading-relaxed">{s.warning}</p>
                            </div>
                          )}

                          {/* Notes */}
                          {s.notes.length > 0 && (
                            <div className="space-y-1 mb-4">
                              {s.notes.map((n, k) => (
                                <p key={k} className="text-white/30 text-xs">* {n}</p>
                              ))}
                            </div>
                          )}

                          <Link to="/rezerwacja" data-testid={`service-book-${i}`} className="btn-primary text-xs">
                            Zarezerwuj <ArrowRight size={12} />
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </FadeUp>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <FadeUp className="mt-12 text-center">
          <p className="text-white/30 text-sm mb-6 font-outfit">Nie jesteś pewna który wariant wybrać?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rezerwacja" data-testid="services-cta-book" className="btn-primary">
              Zarezerwuj Wizytę <ArrowRight size={14} />
            </Link>
            <Link to="/cennik" data-testid="services-cta-cennik" className="btn-secondary">
              Pełny Cennik ze Stopniami
            </Link>
          </div>
        </FadeUp>
      </section>

      <Footer />
    </div>
  );
}
